import { ACTIVITY_PHRASES, OWN_EVIDENCE_ONLY } from './resume-skill-phrases';
import { isOrdinaryWord, termsIn } from './resume-tailor';

/**
 * T.10 S15 (D96). The Skills section from specific skills, pure, used by
 * the wizard on every rebuild so What we know, the resume and the preview
 * agree. In order: tools the member named in their own words, a phrase for
 * each task they picked, the main work activities of their held job
 * profiles; the broad O*NET skill and knowledge names only when none of
 * those is known. A tool only common in their occupation is never added: it
 * is offered, to be tapped if true.
 */

export type SkillKind = 'tool' | 'does' | 'activity' | 'element';

export interface ComposedSkill {
  key: string;
  label: string;
  why: string;
  source: string;
  kind: SkillKind;
}

export interface OccupationTool {
  name: string;
  hot: boolean;
  /** The occupation it is listed for, in O*NET's words. */
  occupation?: string | null;
}

export interface ToolOffer {
  name: string;
  hot: boolean;
  why: string;
}

export interface SkillJob {
  title: string;
  civilianTitle?: string;
  /** The member's own lines: wins and typed tasks. */
  lines: string[];
  tasks: { label: string; skills?: string[] }[];
}

export interface ActivityRow {
  gwaId: string;
  name: string;
  importance: number;
  goalImportance: number | null;
}

export interface SkillInput {
  /** Kept jobs only (S13: a removed job is not kept). */
  jobs: SkillJob[];
  /** Skills the member added, typed, tapped or brought from a resume: already on the resume, never repeated. */
  ownSkills: string[];
  tools: OccupationTool[];
  activities: ActivityRow[];
  /** Today's ranked O*NET names (spec 9.2): the fallback. */
  elements: { key: string; label: string; why: string }[];
}

export const ACTIVITIES_MAX = 6;
export const ACTIVITY_MIN_IMPORTANCE = 3;
export const OFFERS_MAX = 8;

/** Vendor words a posting or a member often leaves off a product name. */
const VENDORS = new Set(['microsoft', 'oracle', 'ibm', 'cisco', 'google', 'apple', 'adobe', 'sap', 'amazon', 'vmware', 'autodesk', 'intuit', 'salesforce', 'atlassian', 'dell', 'hp', 'citrix', 'symantec', 'juniper', 'red hat', 'comptia']);

/** A tool's name and its short form without a leading vendor word ("Microsoft Active Directory", "Active Directory"). */
export function toolForms(name: string): string[] {
  const n = name.trim();
  const m = /^(\S+(?:\s+Hat)?)\s+(.+)$/.exec(n);
  if (m && VENDORS.has(m[1].toLowerCase()) && m[2].length >= 2 && !/^\d/.test(m[2])) {
    return [n, m[2]];
  }
  return [n];
}

const lower = (s: string) => s.toLowerCase();

/** The tools a text names, by their full or short form, in tool order. A one-word form like "Excel" counts only as written. */
export function toolsNamed(text: string, tools: readonly OccupationTool[]): string[] {
  const forms = new Map<string, string>();
  const exact = new Set<string>();
  for (const t of tools) {
    for (const f of toolForms(t.name)) {
      forms.set(f, t.name);
      if (isOrdinaryWord(f)) {
        exact.add(f);
      }
    }
  }
  const hit = new Set(termsIn(text, [...forms.keys()], exact).map((f) => forms.get(f) as string));
  return tools.filter((t) => hit.has(t.name)).map((t) => t.name);
}

/** S17 (D98): specific skills plus broad names, in all, before Show all. */
export const SKILLS_TOP_UP = 8;

const STOP = new Set(['and', 'or', 'of', 'the', 'a', 'an', 'to', 'for', 'with', 'in', 'on', 'at', 'by', 'from', 'other', 'others']);
const stems = (s: string) =>
  lower(s)
    .split(/[^a-z0-9]+/)
    .filter((w) => w && !STOP.has(w))
    .map((w) => w.slice(0, 5));

/**
 * S17 (D98): a specific label covers a broad name when every content word of
 * the broad name is in it, or the two share 2 content words. Words compare on
 * their first 5 letters ("Monitoring", "monitor"; "Repairing", "repair").
 */
export function covers(specific: string, broadName: string): boolean {
  const have = new Set(stems(specific));
  const want = [...new Set(stems(broadName))];
  const shared = want.filter((w) => have.has(w)).length;
  return want.length > 0 && (shared === want.length || shared >= 2);
}

const short = (s: string) => (s.length > 70 ? `${s.slice(0, 67).trim()}...` : s);

export function specificSkills(x: SkillInput): { skills: ComposedSkill[]; offers: ToolOffer[] } {
  const seen = new Set(x.ownSkills.map(lower));
  const out: ComposedSkill[] = [];
  const add = (s: ComposedSkill) => {
    if (s.label && !seen.has(lower(s.label))) {
      seen.add(lower(s.label));
      out.push(s);
    }
  };
  const byHot = [...x.tools].sort((a, b) => Number(b.hot) - Number(a.hot));
  // (1) Tools in the member's own words: a win, a typed task, a task they picked.
  const named = new Map<string, string>();
  for (const j of x.jobs) {
    for (const text of [...j.lines, ...j.tasks.map((t) => t.label)]) {
      toolsNamed(text, byHot).forEach((t) => named.has(t) || named.set(t, text));
    }
  }
  for (const t of byHot.filter((t) => named.has(t.name))) {
    add({ key: `tool:${lower(t.name)}`, label: t.name, why: `You named it: "${short(named.get(t.name) as string)}"`, source: 'Your own words', kind: 'tool' });
  }
  // ... then a phrase for each task they picked.
  for (const j of x.jobs) {
    for (const t of j.tasks) {
      for (const p of t.skills ?? []) {
        add({ key: `does:${lower(p)}`, label: p, why: `From a task you picked: ${t.label}.`, source: 'Your tasks', kind: 'does' });
      }
    }
  }
  // (3) The main kinds of work in the jobs they held; leading and the like only from their own tasks.
  const score = (a: ActivityRow) => Number(a.importance || 0) + 2 * Number(a.goalImportance || 0);
  x.activities
    .filter((a) => ACTIVITY_PHRASES[a.gwaId] && !OWN_EVIDENCE_ONLY.has(a.gwaId) && Number(a.importance) >= ACTIVITY_MIN_IMPORTANCE)
    .sort((a, b) => score(b) - score(a) || a.gwaId.localeCompare(b.gwaId))
    .slice(0, ACTIVITIES_MAX)
    .forEach((a) =>
      add({ key: `activity:${a.gwaId}`, label: ACTIVITY_PHRASES[a.gwaId], why: `A main kind of work in the jobs you have held (O*NET: ${a.name}).`, source: 'Your jobs (SkillsDNA)', kind: 'activity' })
    );
  // (4) S17 (D98): the broad names fill up to 8 in all (the member's own skills count); with nothing specific, all of them as before.
  const specific = x.ownSkills.length + out.length;
  const labels = [...x.ownSkills, ...out.map((s) => s.label)];
  let room = specific ? SKILLS_TOP_UP - specific : x.elements.length;
  for (const e of x.elements) {
    if (room <= 0) {
      break;
    }
    if (e.label && !seen.has(lower(e.label)) && !labels.some((l) => covers(l, e.label))) {
      add({ key: e.key, label: e.label, why: e.why, source: 'Skills Survey and your jobs', kind: 'element' });
      room--;
    }
  }
  const offers = byHot
    .filter((t) => !named.has(t.name) && !x.ownSkills.some((o) => toolForms(t.name).some((f) => lower(f) === lower(o))))
    .slice(0, OFFERS_MAX)
    .map((t) => ({
      name: t.name,
      hot: t.hot,
      why: `O*NET lists it as technology often used by ${t.occupation || 'people in your occupation'}. CPD does not know you used it.`
    }));
  return { skills: out, offers };
}
